import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * News tooltip card. Rendered absolutely inside the chart container.
 *
 * Positioning is intentionally simple:
 *   - X follows the cursor / clicked bar (with right-edge flip and clamp).
 *   - Y is parked just above the news band so the card sits above the news
 *     bars rather than chasing the cursor's Y. The card's z-index keeps it
 *     visually above the chart line/area underneath.
 */
const NEWS_BAND_TOP_FRACTION = 0.9; // matches MainChart's `inNewsBand` threshold

const NewsTooltipOverlay = ({
  active,
  x,
  y,
  point,
  locked,
  onLeaveWhileLocked,
  onShowAllNews,
  containerRef,
}) => {
  const overlayRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!active || !overlayRef.current || !containerRef?.current) return;

    const overlayRect = overlayRef.current.getBoundingClientRect();
    const parentRect = containerRef.current.getBoundingClientRect();
    const GAP = 12;
    const EDGE = 8;

    // Horizontal: nudge the card slightly to the right of the bar, then clamp
    // inside the chart. We deliberately don't flip — flipping pushes the
    // card's left edge ~cardWidth pixels to the left of the cursor, which
    // makes it look unrelated to bars near the right edge. Clamping keeps the
    // card adjacent to the bar (its right edge sits next to the cursor when
    // the bar is at the right of the chart).
    const rawLeft = x + GAP;
    const maxLeft = Math.max(EDGE, parentRect.width - overlayRect.width - EDGE);
    const left = Math.max(EDGE, Math.min(rawLeft, maxLeft));

    // Vertical: anchor above the hovered/clicked bar so the overlay stays
    // close to the active news volume point. Clamp so the card never escapes
    // the chart top.
    const rawTop = (typeof y === 'number' ? y : parentRect.height * NEWS_BAND_TOP_FRACTION) - overlayRect.height - GAP;
    const top = Math.max(EDGE, rawTop);

    setPosition({ left, top });
  }, [active, x, y, containerRef, point]);

  if (!active || !point) return null;
  const articles = point.newsArticles || [];
  if (articles.length === 0) return null;

  return (
    <div
      ref={overlayRef}
      className={`news-tooltip-overlay ${locked ? 'locked' : ''}`}
      onMouseLeave={locked ? onLeaveWhileLocked : undefined}
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? 'visible' : 'hidden',
        pointerEvents: locked ? 'auto' : 'none',
      }}
    >
      <div className="news-tooltip-expanded glass-card-ui">
        <div className="news-tooltip-match-heading">
          {articles.length} MATCHING NEWS
        </div>
        <ul className="news-tooltip-articles">
          {articles.slice(0, 3).map((article) => (
            <li key={article.id} className="news-tooltip-article">
              {article.title}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="news-tooltip-show-all"
          onClick={(e) => {
            e.stopPropagation();
            onShowAllNews?.(point);
          }}
        >
          SHOW ALL NEWS
        </button>
      </div>
    </div>
  );
};

export default NewsTooltipOverlay;
